import type { ComponentType } from 'react';
import type {
  DropdownIndicatorProps,
  OptionProps,
  InputProps,
  ClearIndicatorProps,
  MultiValueRemoveProps,
  NoticeProps,
  StylesConfig,
  GroupBase,
  MenuListProps,
  MultiValueProps,
} from 'react-select';
import { components } from 'react-select';
import { Input } from 'react-select/animated';
import type { MenuPortalProps } from 'react-select/dist/declarations/src/components/Menu';
import { useTheme } from 'styled-components';

import Icons from '../../Elements/Icons/index.ts';
import { Loader } from '../../Elements/Loader/index.tsx';
import {
  LoaderWrapper,
  MenuPortalWrapper,
  OptionDescription,
  OptionTitle,
  CheckWrapper,
  MenuListContent,
  FIXED_MULTI_VALUE_CLASS,
} from './styled.ts';
import type { Option } from './types.ts';

interface CustomInputProps extends InputProps {
  selectProps: InputProps['selectProps'] & {
    dataTestId?: string;
    name?: string;
    readOnly?: boolean;
  };
}

// CustomInput adds `name` to the `react-select`s main input to support focus operations by name (for final-form-focus)
const CustomInput: ComponentType<InputProps> = (props: CustomInputProps) => {
  if (!Input) {
    return null;
  }
  const { selectProps } = props;
  const { dataTestId, name, readOnly } = selectProps;
  return <Input {...props} data-testid={dataTestId} name={name} readOnly={readOnly ?? false} autoComplete='off' />;
};

export const CustomClearIcon = <OT extends Option, IsMulti extends boolean>(props: ClearIndicatorProps<OT, IsMulti>) => {
  const theme = useTheme();
  return (
    <components.ClearIndicator {...props}>
      <Icons.Clear filled color={theme.colors.grey70_1} size={20} />
    </components.ClearIndicator>
  );
};

export const CustomLoadingIndicator = () => {
  const theme = useTheme();
  return (
    <LoaderWrapper>
      <Loader size='14px' fill={theme.colors.grey100_1} background='transparent' />
    </LoaderWrapper>
  );
};

export const CustomLoadingMessage = <OT extends Option, IsMulti extends boolean>(props: NoticeProps<OT, IsMulti>) => {
  const theme = useTheme();
  return (
    <components.LoadingMessage {...props}>
      <Loader size='14px' fill={theme.colors.grey100_1} />
    </components.LoadingMessage>
  );
};

export const CustomDropdownIcon = <OT extends Option, IsMulti extends boolean>(props: DropdownIndicatorProps<OT, IsMulti>) => (
  <components.DropdownIndicator {...props}>
    <Icons.Chevron align='bottom' size={20} />
  </components.DropdownIndicator>
);

export const CustomOption = <OT extends Option, IsMulti extends boolean>({ children, isSelected, ...props }: OptionProps<OT, IsMulti>) => (
  <components.Option isSelected={isSelected} {...props}>
    <OptionTitle data-testid='option-label'>
      {children}
      {isSelected && (
        <CheckWrapper>
          <Icons.Checkmark size={20} />
        </CheckWrapper>
      )}
    </OptionTitle>
    {props?.data?.description ? <OptionDescription>{props?.data?.description}</OptionDescription> : null}
  </components.Option>
);

export const CustomMultiValueRemove = <OT extends Option, IsMulti extends boolean>(props: MultiValueRemoveProps<OT, IsMulti>) => {
  const theme = useTheme();
  const { innerProps, data } = props;
  const { fixed } = data;

  return (
    <components.MultiValueRemove
      {...props}
      innerProps={
        fixed
          ? {
              ...innerProps,
              onClick: undefined,
              onMouseDown: undefined,
              onTouchEnd: undefined,
            }
          : innerProps
      }>
      <Icons.Clear size={16} color={theme.colors.grey100_1} />
    </components.MultiValueRemove>
  );
};

export const CustomMenuPortal = <OT extends Option, IsMulti extends boolean>({
  children,
  ...props
}: MenuPortalProps<OT, IsMulti, GroupBase<OT>>) => (
  <components.MenuPortal {...props}>
    <MenuPortalWrapper>{children}</MenuPortalWrapper>
  </components.MenuPortal>
);

export const CustomMenuList = <OT extends Option, IsMulti extends boolean>({
  children,
  ...restProps
}: MenuListProps<OT, IsMulti, GroupBase<OT>>) => (
  <components.MenuList {...restProps}>
    <MenuListContent>{children}</MenuListContent>
  </components.MenuList>
);

export const commonProps = {
  classNamePrefix: 'select',
  hideSelectedOptions: false,
  menuPortalTarget: document.querySelector('body'),
  menuPlacement: 'auto',
  classNames: {
    multiValue: ({ data }: MultiValueProps<Option, true>) => {
      if (data.fixed) {
        return FIXED_MULTI_VALUE_CLASS;
      }

      return '';
    },
  },
  components: {
    Input: CustomInput,
    ClearIndicator: CustomClearIcon,
    DropdownIndicator: CustomDropdownIcon,
    Option: CustomOption,
    LoadingIndicator: CustomLoadingIndicator,
    LoadingMessage: CustomLoadingMessage,
    MultiValueRemove: CustomMultiValueRemove,
    MenuPortal: CustomMenuPortal,
    MenuList: CustomMenuList,
  },
};

export const customStyles: StylesConfig<Option, boolean, GroupBase<Option>> = {
  control: (provided) => ({
    ...provided,
    minHeight: '36px',
  }),
  // portal need higher z-index than modal to be on top
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};
